import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Logo } from "../../components/Logo";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { useAuth } from "../../lib/auth";
import { formatApiErrorDetail } from "../../lib/api";

export default function Register() {
  const [params] = useSearchParams();
  const ref = params.get("ref") || "";
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "customer" });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) { toast.error("Please fill name, email and password"); return; }
    setLoading(true);
    try {
      await register({ ...form, ref });
      toast.success("Account created! Welcome to Genesis Group.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed");
    } finally { setLoading(false); }
  };

  const ROLES = [
    { v: "customer", l: "Customer", d: "Shop & connect" },
    { v: "agent", l: "Agent / DSA", d: "Add clients, earn" },
    { v: "affiliate", l: "Affiliate", d: "Refer & earn" },
  ];
  const field = "bg-white border-cream-300 h-11 text-soil-700 placeholder:text-soil-400";

  return (
    <div className="min-h-screen bg-cream-100 bharat-pattern flex items-center justify-center p-4 py-12" data-testid="register-page">
      <div className="absolute top-6 left-6"><Logo onLight /></div>
      <div className="w-full max-w-md card-cream rounded-2xl p-8">
        <h1 className="font-heading text-3xl font-extrabold text-forest-800">Join the Genesis Ecosystem</h1>
        <p className="text-soil-500 text-sm mt-1">Connect with Genesis Group and become part of a growing business network. {ref && <span className="text-terracotta-600">(via referral)</span>}</p>
        <form onSubmit={submit} className="space-y-4 mt-6" data-testid="register-form">
          <div>
            <div className="text-soil-600 text-sm mb-2">Register as</div>
            <RadioGroup value={form.role} onValueChange={(v) => set("role", v)} className="grid grid-cols-3 gap-2">
              {ROLES.map((r) => (
                <label key={r.v} className={`cursor-pointer rounded-xl border p-2.5 text-center transition-colors ${form.role === r.v ? "border-forest-600 bg-forest-100" : "border-cream-300 hover:border-cream-300"}`}>
                  <RadioGroupItem value={r.v} className="sr-only" data-testid={`register-role-${r.v}`} />
                  <div className={`text-sm font-semibold ${form.role === r.v ? "text-forest-700" : "text-soil-700"}`}>{r.l}</div>
                  <div className="text-[10px] text-soil-400 mt-0.5">{r.d}</div>
                </label>
              ))}
            </RadioGroup>
          </div>
          <Input data-testid="register-name-input" placeholder="Full Name *" value={form.name} onChange={(e) => set("name", e.target.value)} className={field} />
          <Input data-testid="register-email-input" type="email" placeholder="Email *" value={form.email} onChange={(e) => set("email", e.target.value)} className={field} />
          <Input data-testid="register-phone-input" placeholder="Phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={field} />
          <Input data-testid="register-password-input" type="password" placeholder="Password (min 6 chars) *" value={form.password} onChange={(e) => set("password", e.target.value)} className={field} />
          <Button type="submit" disabled={loading} data-testid="register-submit-button" className="w-full forest-gradient text-cream-50 font-bold h-12 rounded-full">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Create Account"}
          </Button>
        </form>
        <div className="mt-2">
          <div className="flex items-center gap-3 my-4"><div className="h-px bg-cream-300 flex-1" /><span className="text-soil-400 text-xs">OR</span><div className="h-px bg-cream-300 flex-1" /></div>
          <button type="button" onClick={googleLogin} data-testid="google-login-button" className="w-full flex items-center justify-center gap-3 h-12 rounded-full bg-white border border-cream-300 text-soil-700 font-semibold hover:bg-cream-50 transition-colors">
            <span className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-500 via-red-500 to-yellow-400 flex items-center justify-center text-white text-[11px] font-bold">G</span>
            Continue with Google
          </button>
        </div>
        <p className="text-soil-500 text-sm mt-5 text-center">Already have an account? <Link to="/login" className="text-terracotta-600 font-semibold">Sign in</Link></p>

        <div className="mt-6 pt-5 border-t border-cream-300">
          <h2 className="font-heading text-lg font-semibold text-forest-800">Why join Genesis Group?</h2>
          <p className="text-soil-500 text-sm mt-2">
            Creating a Genesis account opens the door to the All-in-One Business Center — a Swadeshi ecosystem that connects
            businesses, shoppers and entrepreneurs across a modern Bharat. Register as a Customer to shop and connect across
            ten verticals, as an Agent/DSA to add clients and earn commissions, or as an Affiliate to refer and grow a team.
          </p>
          <p className="text-soil-500 text-sm mt-2">
            Every member gets a personal dashboard with a five-level team tree, transparent ranks and rewards, secure
            document management, lead tracking and instant notifications. Payouts and withdrawals stay fully in your control
            with clear approval steps and no hidden auto-deductions. From Genesis Finance (Loan Doctor) and Agro-Dairy to
            E-commerce, Green Energy, Healthcare, Education, Import-Export, Marketing and IT/VFX, your single Genesis login
            grows with you. Rooted in Bharat, connected to opportunity.
          </p>
        </div>
      </div>
    </div>
  );
}
